import * as actionTypes from "../actionTypes/packListingActionTypes";
import { getActionBaseProvider, getActionProvider } from "../../../../Application/actions/actionsBuilder";
import {
  type EnablePackTrialRequest,
  type PackListingView,
  type PackRequest,
  type EnablePackRequest,
} from "../../types/state";
import dataService from "../../../../Application/services/dataServices/typedDataService";
import { type AppDispatch } from "../../../../Application/globaltypes/redux";
import { sendTransientNotification } from "../../../../Notifications/state/notificationsActions";
import { type DistributedOpUpdateParams } from "../../../../../interfaces/updateParams";
import backgroundTask from "../../../../BackgroundTasks/backgroundTask";

export const showPackRequestModal = getActionBaseProvider(actionTypes.showPackRequestModal);

export const hidePackRequestModal = getActionBaseProvider(actionTypes.hidePackRequestModal);

export const getPackListing = (id: number) => {
  const begin = getActionBaseProvider(actionTypes.getPackOverviewBegin);
  const success = getActionProvider<PackListingView>(actionTypes.getPackOverviewSuccess);
  const failure = getActionProvider<Error>(actionTypes.getPackOverviewFailure);

  return (dispatch: Function) => {
    dispatch(begin());
    dataService.packs
      .getPackListingAsync(id)
      .then((result: { data: PackListingView }) => dispatch(success(result.data)))
      .catch((error: Error) => dispatch(failure(error)));
  };
};

export const requestPack = (packRequest: PackRequest, isAdditionalPurchaseEnabled: boolean) => {
  const begin = getActionBaseProvider(actionTypes.requestPackBegin);
  const success = getActionProvider<any>(actionTypes.requestPackSuccess);
  const failure = getActionProvider<Error>(actionTypes.requestPackFailure);

  return async (dispatch: AppDispatch) => {
    dispatch(begin());
    try {
      await dataService.packs.requestPackAsync(packRequest, isAdditionalPurchaseEnabled);
      dispatch(success({}));
      dispatch(sendTransientNotification({ title: "Success!", message: "Request sent successfully", type: 0 }, 5));
    } catch (error) {
      dispatch(failure(error));
    }
  };
};

export const enablePackTrial = (packRequest: EnablePackTrialRequest, onCompleted?: () => void) => {
  return async (dispatch: AppDispatch) => {
    const updateParams: DistributedOpUpdateParams = {
      id: `EnablePackTrial_${Date.now()}`,
      title: "Starting Pack Trial",
      getOperationProps: async () => {
        const { data } = await dataService.packs.enablePackTrialAsync(packRequest);
        return {
          operationId: data.operationId,
          stepsIds: data.stepsIds,
          statusUrl: data.statusUrl,
        };
      },
      successTransientMessage: "Pack trial successfully enabled",
      failureTransientMessage: "Failed to enable pack trial",
      indeterminate: true,
      onCompleted,
    };

    await backgroundTask.initializeDistributedBackgroundTask(updateParams, dispatch);
  };
};

export const enablePack = (
  packRequest: EnablePackRequest,
  isPaidPacksEnabled: boolean = false,
  onCompleted?: () => void,
) => {
  const { operationTitle, success, failure } = isPaidPacksEnabled
    ? {
        operationTitle: "Adding Pack to Account",
        success: "Pack successfully added to account",
        failure: "Failed to add pack to account",
      }
    : {
        operationTitle: "Purchasing Pack",
        success: "Pack successfully purchased",
        failure: "Failed to purchase pack",
      };

  return async (dispatch: AppDispatch) => {
    const updateParams: DistributedOpUpdateParams = {
      id: `PurchasePack_${Date.now()}`,
      title: operationTitle,
      getOperationProps: async () => {
        const { data } = await dataService.packs.enablePackAsync(packRequest);
        return {
          operationId: data.operationId,
          stepsIds: data.stepsIds,
          statusUrl: data.statusUrl,
        };
      },
      successTransientMessage: success,
      failureTransientMessage: failure,
      indeterminate: true,
      onCompleted,
    };

    await backgroundTask.initializeDistributedBackgroundTask(updateParams, dispatch);
  };
};
